/* eslint-disable brace-style  */
import {createSelector} from 'reselect';
import get from 'lodash/get';

import {getListing, getListingExtended, getTranslations} from './store';
import {VRBO_PDP_WEB_INQUIRY_THROTTLE} from '../abacus/experiments';
import {evaluateExperiment} from '../utils/abacusHelper';

const getAverageRating = createSelector(
    [getListing],
    (listing) => {
        return listing.averageRating;
    }
);

const getBathroomCountDisplay = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'spaces.spacesSummary.bathroomCountDisplay', null);
    }
);
const getToiletOnlyCountDisplay = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'spaces.spacesSummary.toiletOnlyCountDisplay', null);
    }
);
const getBathroomCombinedCountDisplay = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'spaces.spacesSummary.bathroomCombinedCountDisplay', null);
    }
);

const getBathrooms = createSelector(
    [getListing],
    (listing) => {
        return listing.bathrooms || {};
    }
);

// TODO: Replace getBathrooms with getBathroomCountDisplay and getToiletOnlyCountDisplay
// after VRBO_WEB_PDP_SUMMARY completes to make terminology more consistent
const getBathroomStrings = createSelector(
    [getTranslations, getBathrooms],
    (labels, bathrooms) => {
        return {
            bathroomsFull: (bathrooms.full && `${labels.bathrooms}: ${bathrooms.full}`) || null,
            bathroomsHalf: (bathrooms.half && `${labels.halfBaths}: ${bathrooms.half}`) || null,
            bathroomsToiletOnly:
                (bathrooms.toiletOnly && `${labels.toiletOnlyRooms}: ${bathrooms.toiletOnly}`) ||
                null
        };
    }
);

const getBedCountDisplay = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'spaces.spacesSummary.bedCountDisplay', null);
    }
);

const getBedrooms = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'spaces.spacesSummary.bedroomCount', null);
    }
);

const getBedroomCountDisplay = createSelector(
    [getListing, getTranslations],
    (listing, translations) => {
        const bedroomCountString = get(listing, 'spaces.spacesSummary.bedroomCountDisplay', null);
        return bedroomCountString || translations.studio;
    }
);

const getBearStayReviewData = createSelector(
    [getListing],
    (listing) => listing.bearStayReview
);

const getBookingType = createSelector(
    [getListing],
    (listing) => {
        return {
            onlineBookable: listing.onlineBookable,
            instantBook: listing.instantBookable,
            payPerBooking: listing.payPerBooking
        };
    }
);

const getDetailPageUrl = createSelector(
    [getListing],
    (listing) => {
        return listing.detailPageUrl;
    }
);

const getGeoCode = createSelector(
    [getListing],
    (listing) => {
        return listing.geoCode;
    }
);

const getGeography = createSelector(
    [getListing],
    (listing) => {
        return listing.geography;
    }
);

const getHostType = createSelector(
    [getListing],
    (listing) => {
        return {
            owner: listing.ownerManaged,
            pm: !listing.ownerManaged,
            ipm: listing.integratedPropertyManager
        };
    }
);

const getLegacyLodgingPolicy = createSelector(
    [getListing],
    (listing) => {
        return listing.legacyLodgingPolicy;
    }
);

const getListingAmenities = createSelector(
    [getListing],
    (listing) => {
        return listing.amenities;
    }
);

const getListingAmenitiesFeatured = createSelector(
    [getListing],
    (listing) => {
        return listing.featuredAmenities;
    }
);

const getAllFeaturedAmenitiesRanked = createSelector(
    [getListing],
    (listing) => {
        return listing.allFeaturedAmenitiesRanked;
    }
);

const getListingAvailabilityCalendar = createSelector(
    [getListing],
    (listing) => {
        return listing.availabilityCalendar;
    }
);

const getListingCarousel = createSelector(
    [getListing],
    (listing) => {
        return listing.carousel;
    }
);

const getListingHeadline = createSelector(
    [getListing],
    (listing) => {
        return listing.headline;
    }
);

const getIsIpmNonGuaranteedPricing = createSelector(
    [getListing],
    ({integratedPropertyManager, ipmGuaranteedPricingActive}) =>
        (integratedPropertyManager && !ipmGuaranteedPricingActive) || false
);

const getListingHouseRules = createSelector(
    [getListing],
    (listing) => {
        return listing.houseRules;
    }
);

// Extended
const getGeoRelativeDistance = createSelector(
    [getListingExtended],
    (listingExtended) => {
        const {geoRelativeDistances} = listingExtended;
        return get(geoRelativeDistances, 'items.0.relations.0.text', null);
    }
);

const getRankedBadges = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'badges.rankedBadges', []);
    }
);

const getPricingBadges = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'badges.pricingBadges', []);
    }
);

const getSuperlativeBadges = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'badges.superlativeBadges', []);
    }
);

const getReviewHighlightBadges = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'badges.reviewHighlightBadges', []);
    }
);

const getSpaces = createSelector(
    [getListing],
    (listing) => {
        return listing.spaces || {};
    }
);

const getSpacesDiningRooms = createSelector(
    [getListing],
    ({spacesDiningRooms}) => {
        return (spacesDiningRooms || {}).diningRooms || [];
    }
);

const getUnitContentItems = createSelector(
    [getListing],
    (listing) => {
        return listing.unitContentItems || [];
    }
);

const getUnitContentItemsSpaces = createSelector(
    [getListing],
    ({unitContentItemsSpaces}) => unitContentItemsSpaces || []
);

const getPropertyContentItems = createSelector(
    [getListing],
    (listing) => {
        return listing.propertyContentItems || [];
    }
);

const getUnitMetadata = createSelector(
    [getListing],
    (listing) => {
        return listing.unitMetadata || [];
    }
);

const getListingByOwnerProperties = createSelector(
    [getListingExtended],
    (listingExtended) => listingExtended.byOwnerProperties
);

const getListingRecommendedProperties = createSelector(
    [getListingExtended],
    (listingExtended) => listingExtended.recommendedProperties
);

const getRecommendedPropertiesAlternativeTitle = createSelector(
    [getListingExtended],
    (listingExtended) => listingExtended.recommendedPropertiesAlternativeTitle
);

const getByOwnerClientRequestId = createSelector(
    [getListingExtended],
    (listingExtended) => listingExtended.byOwnerClientRequestId
);

const getInquiryClientRequestId = createSelector(
    [getListingExtended],
    (listingExtended) => listingExtended.inquiryClientRequestId
);

const getListingInquiryProperties = createSelector(
    [getListingExtended],
    (listingExtended) => listingExtended.inquiryProperties
);

// Start here
const getListingIds = createSelector(
    [getListing],
    (listing) => {
        return {
            listingId: listing.listingId,
            propertyId: listing.propertyId
        };
    }
);

const getUnitApiURL = createSelector(
    [getListing],
    (listing) => listing.unitApiUrl
);

const getLocalizedDescriptionMetadata = createSelector(
    [getListing],
    (listing) => {
        return listing.localizedDescriptionMetadata;
    }
);

const getMinStayRange = createSelector(
    [getListing],
    (listing) => {
        return listing.minStayRange;
    }
);

const getMultiUnit = createSelector(
    [getListing],
    (listing) => {
        return {
            isMultiUnit: listing.multiUnitProperty,
            unitName: listing.unitName,
            unitNumber: listing.unitNumber,
            unitDescription: listing.unitDescription
        };
    }
);

const getPetsAllowed = createSelector(
    [getListing],
    (listing) => {
        return listing.petsAllowed;
    }
);

const getPmProfile = createSelector(
    [getListing],
    (listing) => {
        return listing.propertyManagerProfile || {};
    }
);

const getPreferredLocation = createSelector(
    [getListing],
    (listing) => {
        return listing.preferredLocation;
    }
);

const getPropertyName = createSelector(
    [getListing],
    (listing) => {
        return listing.propertyName;
    }
);

const getPropertyType = createSelector(
    [getListing],
    (listing) => {
        return listing.propertyType;
    }
);

const getRateDetails = createSelector(
    [getListing],
    (listing) => {
        return listing.rateDetails;
    }
);

const getRateSummary = createSelector(
    [getListing],
    (listing) => {
        return listing.rateSummary || {};
    }
);

const getRegions = createSelector(
    [getListing],
    (listing) => {
        return listing.regions;
    }
);

const getSearchLandingPages = createSelector(
    [getListing],
    (listing) => {
        return listing.searchLandingPages;
    }
);

const getSleeps = createSelector(
    [getListing],
    (listing) => {
        return listing.sleeps;
    }
);

const getSleepsDisplay = createSelector(
    [getListing],
    (listing) => {
        return listing.sleepsDisplay;
    }
);

const getSpu = createSelector(
    [getListing],
    (listing) => {
        return listing.spu;
    }
);

const getTakesInquiries = createSelector(
    [getListing],
    (listing) => {
        const disableInquiries = evaluateExperiment({
            name: VRBO_PDP_WEB_INQUIRY_THROTTLE,
            variant: 1,
            deferLogging: true
        });
        return listing.takesInquiries && !disableInquiries;
    }
);

const getTaxRate = createSelector(
    [getListing],
    (listing) => {
        return listing.taxRate;
    }
);

const getThumbnailUrl = createSelector(
    [getListing],
    (listing) => {
        return listing.thumbnailUrl;
    }
);

const getAccomodationSummary = createSelector(
    [getListing],
    (listing) => {
        return listing.accomodationSummary;
    }
);

const getAreaDisplay = createSelector(
    [getSpaces],
    (spaces) => {
        const area = get(spaces, 'spacesSummary.area', {});
        const {areaDisplay} = area;

        return areaDisplay;
    }
);

const getContact = createSelector(
    [getListing],
    (listing) => {
        return listing.contact;
    }
);

const getDescription = createSelector(
    [getListingExtended, getListing],
    (listingExtended, listing) => {
        return (listingExtended.description || listing.description || '').trim();
    }
);

const getIpmGuaranteedPricingActive = createSelector(
    [getListing],
    (listing) => {
        return listing.ipmGuaranteedPricingActive;
    }
);

const getPropertyNotes = createSelector(
    [getListing],
    (listing) => {
        return listing.propertyNotes;
    }
);

const getListingIsFamilyFriendly = createSelector(
    [getListing],
    (listing) => {
        return listing.isFamilyFriendly;
    }
);

const getVirtualTourData = createSelector(
    [getListingCarousel],
    (listingCarousel) => {
        // eslint-disable-next-line no-debugger
        return {
            virtualTourId: listingCarousel.virtualTourId,
            virtualTourUrl: listingCarousel.virtualTourUrl,
            virtualTourHeroImage: listingCarousel.virtualTourHeroImage,
            virtualTourViewerVersion: listingCarousel.virtualTourViewerVersion
        };
    }
);

const getVTHelpfulnessHistory = createSelector(
    [getListingExtended],
    (listingExtended) => {
        return listingExtended.vtHelpfulnessHistory;
    }
);

const getPropertyMapThumbnailUrl = createSelector(
    [getListing],
    (listing) => {
        return listing.propertyMapThumbnailUrl;
    }
);

const getPropertyMapThumbnailResponsiveOptions = createSelector(
    [getListing],
    (listing) => {
        return listing.propertyMapThumbnailResponsiveOptions;
    }
);

const getPropertyManagerMessaging = createSelector(
    [getListing],
    (listing) => {
        return listing.propertyManagerMessaging;
    }
);

const getMinStay = createSelector(
    [getMinStayRange, getRateDetails],
    (minStayRange, rateDetails) => {
        if (minStayRange) {
            return minStayRange;
        }

        if (rateDetails.rateSections && rateDetails.rateSections.length > 0) {
            const minStayArray = [];

            rateDetails.rateSections.forEach((rateSection) => {
                if (!isNaN(rateSection.minStay)) {
                    minStayArray.push(rateSection.minStay);
                }
            });

            const minStayLow = Math.min(...minStayArray);
            const minStayHigh = Math.max(...minStayArray);

            return {
                minStayLow,
                minStayHigh
            };
        }

        return {minStayLow: null, minStayHigh: null};
    }
);

const getPriceSummary = createSelector(
    [getListing],
    (listing) => listing.priceSummary
);

const getGlobalMessages = createSelector(
    [getListing],
    (listing) => listing.globalMessages
);

const getThingsOfInterest = createSelector(
    [getListing],
    (listing) => listing.thingsOfInterest
);

const getIndustryHealthAssociations = createSelector(
    [getListing],
    ({industryHealthAssociations}) => industryHealthAssociations
);

const getRegionalHealthGuidelines = createSelector(
    [getListing],
    ({regionalHealthGuidelines}) => regionalHealthGuidelines
);

const getWebRatingBadges = createSelector(
    [getListing],
    (listing) => {
        return get(listing, 'badges.webRatingBadges', []);
    }
);

export {
    getAccomodationSummary,
    getAreaDisplay,
    getAverageRating,
    getRankedBadges,
    getBathroomCountDisplay,
    getBathrooms,
    getBathroomStrings,
    getBearStayReviewData,
    getBedCountDisplay,
    getBedroomCountDisplay,
    getBedrooms,
    getBookingType,
    getBathroomCombinedCountDisplay,
    getContact,
    getDescription,
    getDetailPageUrl,
    getGeoCode,
    getGeography,
    getGeoRelativeDistance,
    getGlobalMessages,
    getToiletOnlyCountDisplay,
    getHostType,
    getIndustryHealthAssociations,
    getIpmGuaranteedPricingActive,
    getIsIpmNonGuaranteedPricing,
    getLegacyLodgingPolicy,
    getListingAmenities,
    getListingAmenitiesFeatured,
    getAllFeaturedAmenitiesRanked,
    getListingAvailabilityCalendar,
    getListingCarousel,
    getListingHeadline,
    getListingHouseRules,
    getListingIds,
    getUnitApiURL,
    getListingByOwnerProperties,
    getListingRecommendedProperties,
    getListingInquiryProperties,
    getMinStay,
    getMinStayRange,
    getMultiUnit,
    getPetsAllowed,
    getPmProfile,
    getPreferredLocation,
    getPropertyContentItems,
    getPropertyName,
    getPropertyNotes,
    getPropertyType,
    getRateDetails,
    getRecommendedPropertiesAlternativeTitle,
    getInquiryClientRequestId,
    getByOwnerClientRequestId,
    getRateSummary,
    getRegions,
    getSearchLandingPages,
    getSleeps,
    getSleepsDisplay,
    getSpu,
    getTakesInquiries,
    getTaxRate,
    getThingsOfInterest,
    getThumbnailUrl,
    getSpaces,
    getUnitContentItems,
    getUnitContentItemsSpaces,
    getUnitMetadata,
    getListingIsFamilyFriendly,
    getVirtualTourData,
    getVTHelpfulnessHistory,
    getPropertyMapThumbnailUrl,
    getPropertyMapThumbnailResponsiveOptions,
    getPropertyManagerMessaging,
    getLocalizedDescriptionMetadata,
    getPricingBadges,
    getSpacesDiningRooms,
    getSuperlativeBadges,
    getPriceSummary,
    getRegionalHealthGuidelines,
    getReviewHighlightBadges,
    getWebRatingBadges
};
